import { CrudService } from './CrudService';
import { resources } from '../resources';
import { TrainingVersionDto } from '../models/training/TrainingVersionDto';
import { Args } from './BaseService';
import { AxiosPromise } from 'axios';
import { SlideDto } from '../models/training/SlideDto';
import { WorkplaceQueryParams } from '../params/TrainingVersionQueryParams';
import { ReferenceDto } from '../models/employees/ReferenceDto';
import { CopyVersionRequest } from '../models/version/CopyVersionRequest';
import { CopyVersionHolderToVersionablesRequest } from '../models/version/CopyVersionHolderToVersionablesRequest';
import { CopyVersionHolderToNewVersionableRequest } from '../models/version/CopyVersionHolderToNewVersionableRequest';
import { UpdateContainerOrder } from '../models/workplace/UpdateContainerOrder';
import { AddLanguageVariant } from '../models/training/AddLanguageVariant';
import { SubmitAnswersRequest } from '../models/questionnaire/SubmitAnswersRequest';
import { SubmitAnswersResponse } from '../models/questionnaire/SubmitAnswersResponse';
import { SignTrainingRequest } from '../models/training/SignTrainingRequest';

export class TrainingVersionService extends CrudService<TrainingVersionDto, WorkplaceQueryParams> {
  constructor() {
    super({
      query: resources.queryTrainingVersions,
      get: resources.getTrainingVersion,
      create: resources.createTrainingVersion,
      update: resources.updateTrainingVersion,
      delete: resources.deleteTrainingVersion,
      idKey: 'versionId',
    });
  }

  public getSlides(version: string, language: string, args?: Args): AxiosPromise<SlideDto[]> {
    return this.get<SlideDto[]>(resources.getSlides, { versionId: version, language, ...args });
  }

  public addTVLanguageVariant(version: string, payload: AddLanguageVariant, args?: Args): AxiosPromise<void> {
    return this.post<void>(resources.addTVLanguageVariant, payload, { versionId: version, ...args });
  }

  public copyTVLanguageVariant(version: string, language: string, payload: AddLanguageVariant, args?: Args): AxiosPromise<void> {
    return this.post<void>(resources.copyTVLanguageVariant, payload, { versionId: version, language, ...args });
  }

  public deleteTVLanguageVariant(version: string, language: string, args?: Args): AxiosPromise<void> {
    return this.delete<void>(resources.deleteTVLanguageVariant, { versionId: version, language, ...args });
  }

  public createSlide(version: string, payload: SlideDto, args?: Args): AxiosPromise<ReferenceDto> {
    return this.post<ReferenceDto>(resources.createSlide, payload, { versionId: version, ...args });
  }

  public updateSlide(slide: string, payload: SlideDto, args?: Args): AxiosPromise<void> {
    return this.put<void>(resources.updateSlide, payload, { slide, ...args });
  }

  public deleteSlide(slide: string, args?: Args): AxiosPromise<void> {
    return this.delete<void>(resources.deleteSlide, { slide, ...args });
  }

  public changeSlideOrder(version: string, language: string, payload: UpdateContainerOrder, args?: Args): AxiosPromise<void> {
    return this.put<void>(resources.changeSlideOrder, payload, { versionId: version, language, ...args });
  }

  public copyTrainingVersion(version: string, payload: CopyVersionRequest, args?: Args): AxiosPromise<ReferenceDto> {
    return this.post<ReferenceDto>(resources.copyTrainingVersion, payload, { versionId: version, ...args });
  }

  public copyVersionToTrainings(version: string, payload: CopyVersionHolderToVersionablesRequest, args?: Args): AxiosPromise<void> {
    return this.post<void>(resources.copyVersionToTrainings, payload, { versionId: version, ...args });
  }

  public copyVersionToNewTraining(version: string, payload: CopyVersionHolderToNewVersionableRequest, args?: Args): AxiosPromise<ReferenceDto> {
    return this.post<ReferenceDto>(resources.copyVersionToNewTraining, payload, { versionId: version, ...args });
  }

  public submitAnswers(trainingVersionId: string, payload: SubmitAnswersRequest, args?: Args): AxiosPromise<SubmitAnswersResponse> {
    return this.put<SubmitAnswersResponse>(resources.submitTrainingAnswers, payload, { trainingVersionId, ...args });
  }

  public submitPlaygroundAnswers(
    params: { questionnaireVersionId?: string; trainingVersionId?: string },
    payload: SubmitAnswersRequest,
    args?: Args,
  ): AxiosPromise<SubmitAnswersResponse> {
    return this.put<SubmitAnswersResponse>(resources.submitPlaygroundAnswers, payload, { ...params, ...args });
  }

  public signTraining(trainingVersionId: string, payload: SignTrainingRequest, args?: Args): AxiosPromise<void> {
    return this.post<void>(resources.signTraining, payload, { trainingVersionId, ...args });
  }
}
